import {
  RobotStatus,
  ROBOT_STATUS_LABELS,
  VerificationStatus,
  LocationCategory,
  LoaderStatus,
  LOADER_STATUS_LABELS,
  ROBOT_LOCATION_LABELS,
  ROBOT_VERIFICATION_LABELS,
} from '@bhops/shared';
import type { CSSProperties } from 'react';

// §9(아이콘·배지 통일) — 상태 배지는 이 파일 하나에서만 만든다.
// 종전에는 로더 상세·로더 탭·계약 상세·딜 상세가 각자 pill 을 다시 그려
// 패딩(2px 8px vs 3px 10px)·반경(raw 999 vs --r-pill)·색 점 유무가 화면마다
// 달랐다. 기하는 여기서 한 벌로 고정하고, 화면은 variant 만 고른다.
type StatusPillVariant = 'outline' | 'solid';

interface StatusPillProps {
  /** 좌측 색 점. 생략하면 점 없이 라벨만(단색 배지·색 구분이 없는 상태값). */
  color?: string;
  label: string;
  /** outline 변형의 배경 틴트. 생략하면 표면색 + 테두리. */
  tint?: string;
  urgent?: boolean;
  /**
   * 'outline'(기본) — 틴트/테두리 + 본문색 글자. 표·목록의 상태값.
   * 'solid'        — tint 를 채움색으로 쓰고 글자는 반전. 계약/딜처럼
   *                  단계 색 자체가 정보인 헤더 배지.
   */
  variant?: StatusPillVariant;
}

export function StatusPill({ color, label, tint, urgent, variant = 'outline' }: StatusPillProps) {
  const solid = variant === 'solid';
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 8px',
    borderRadius: 'var(--r-pill)',
    fontSize: 11,
    fontWeight: solid ? 600 : 500,
    lineHeight: 1.4,
    background: solid ? (tint ?? 'var(--bg-sunken)') : (tint ?? 'var(--bg-surface)'),
    border: `1px solid ${solid || tint ? 'transparent' : 'var(--border-default)'}`,
    color: solid ? 'var(--fg-inverse)' : 'var(--fg-default)',
    whiteSpace: 'nowrap',
  };
  const dotStyle: CSSProperties = {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: color,
  };
  return (
    <span style={style}>
      {color && <span className={urgent ? 'kuun-urgent' : undefined} style={dotStyle} />}
      {label}
    </span>
  );
}

// Mapping from Prisma enum values (SCREAMING_SNAKE_CASE) to display label + color.
// See decisions.md §C-1 for canonical enum definitions.

// FB2 §1 (결정 F) — 라벨은 공유 정전 소스 ROBOT_STATUS_LABELS(@bhops/shared)
// 를 단일 출처로 소비한다(파일별 라벨 표류 차단). 여기서는 색상/틴트 토큰만
// 보유한다.
//
// 디자인 토큰 메모:
//   READY_TO_SHIP   — 출고 대기. shipping 색상 계열(시안).
//   INSTALL_PENDING — 설치 대기. 전용 --robot-installing(브랜드 블루) 토큰 사용
//                     (좌측 StatusBar·라이프사이클 카드와 일치, 출고 대기 시안과 구분). FB3 §1.
//   IN_STORAGE      — 제조 완료 후 출고 전 보관. stock 색상 계열 재사용.
export const ROBOT_STATUS_TINT: Record<RobotStatus, { color: string; tint: string }> = {
  [RobotStatus.IN_PRODUCTION]: {
    color: 'var(--robot-stock)',
    tint: 'var(--robot-stock-tint)',
  },
  [RobotStatus.READY_TO_SHIP]: {
    color: 'var(--robot-shipping)',
    tint: 'var(--robot-shipping-tint)',
  },
  [RobotStatus.INSTALL_PENDING]: {
    color: 'var(--robot-installing)',
    tint: 'var(--robot-installing-tint)',
  },
  [RobotStatus.DEPLOYED]: {
    color: 'var(--robot-operating)',
    tint: 'var(--robot-operating-tint)',
  },
  [RobotStatus.IN_AS]: {
    color: 'var(--robot-maintenance)',
    tint: 'var(--robot-maintenance-tint)',
  },
  [RobotStatus.RETIRED]: {
    color: 'var(--robot-retired)',
    tint: 'var(--robot-retired-tint)',
  },
  // 2026-09-23 — 띠의 '재고' 카드와 같은 쿨 슬레이트. 종전엔 제조 중과 같은 색이라
  // 창고에 있는 로봇이 뱃지로도 제조 중처럼 보였다.
  [RobotStatus.IN_STORAGE]: {
    color: 'var(--robot-storage)',
    tint: 'var(--robot-storage-tint)',
  },
  // round10 요구2 — 폐기 예정(번트 앰버, retired 레드와 구분).
  [RobotStatus.RETIREMENT_PLANNED]: {
    color: 'var(--robot-retire-planned)',
    tint: 'var(--robot-retire-planned-tint)',
  },
  // 2026-09-18 — '기타'(행사·인증·테스트·사내). 플럼.
  //
  // ⚠ 이 표에 빠진 상태가 오면 아래 `ROBOT_STATUS_TINT[status]` 가 undefined 가
  //   되고 `s.color` 에서 터져 **로봇 목록·상세가 통째로 흰 화면**이 된다.
  //   상태를 늘릴 때 여기를 같이 늘리지 않으면 화면이 죽는다.
  [RobotStatus.OTHER]: {
    color: 'var(--robot-other)',
    tint: 'var(--robot-other-tint)',
  },
};

/** 모르는 상태가 왔을 때 쓰는 중립색(화면을 죽이지 않기 위한 마지막 방어선). */
const UNKNOWN_STATUS_TINT = { color: 'var(--fg-muted)', tint: 'var(--bg-subtle, #f2eee5)' };

export function RobotStatusPill({ status }: { status: RobotStatus }) {
  // ⚠ 2026-09-23 — 표에 없는 상태가 오면 종전엔 `s.color` 에서 터져 **로봇 목록·상세·
  //   고객사 상세가 통째로 오류 화면**이 됐다(최상위 ErrorBoundary). '기타' 를 넣을 때
  //   콘솔보다 서버가 먼저 나가면 실제로 그렇게 된다 — 배포 순서 한 번 어긋남으로 운영
  //   화면이 죽는 구조였다. 이제 모르는 상태는 원문을 중립색으로 보여 주고 산다.
  //   (표를 늘리는 것이 여전히 정답이다 — 이건 늘리는 걸 잊었을 때를 위한 것.)
  const s = ROBOT_STATUS_TINT[status] ?? UNKNOWN_STATUS_TINT;
  const label = ROBOT_STATUS_LABELS[status] ?? String(status);
  return <StatusPill color={s.color} label={label} tint={s.tint} />;
}

// 위치 표기 정본은 @bhops/shared(ROBOT_LOCATION_LABELS) — 기준일 마감본 CSV(회계용)와 같은 말을 쓴다.
// AS_CENTER 는 backward-compat — 선택지에선 숨기지만 legacy 데이터가 그대로 표시되도록 라벨 유지.
const LOCATION_LABEL: Record<LocationCategory, string> = ROBOT_LOCATION_LABELS;

export function LocationPill({ category }: { category: LocationCategory }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 'var(--r-pill)',
        background: 'var(--bg-sunken)',
        color: 'var(--fg-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      {LOCATION_LABEL[category]}
    </span>
  );
}

// 로더 상태 — 로더 상세/로봇 상세 '로더' 탭이 같은 배지를 쓴다.
// 색은 로봇 상태 팔레트를 재사용한다(재고=stock, 장착=operating,
// 수리중=maintenance, 폐기=retired) — 두 화면이 나란히 놓이므로 같은 개념은
// 같은 색이어야 한다.
const LOADER_STATUS_TINT: Record<LoaderStatus, { color: string; tint: string }> = {
  [LoaderStatus.IN_STOCK]: { color: 'var(--robot-stock)', tint: 'var(--robot-stock-tint)' },
  [LoaderStatus.INSTALLED]: {
    color: 'var(--robot-operating)',
    tint: 'var(--robot-operating-tint)',
  },
  [LoaderStatus.IN_REPAIR]: {
    color: 'var(--robot-maintenance)',
    tint: 'var(--robot-maintenance-tint)',
  },
  [LoaderStatus.RETIRED]: { color: 'var(--robot-retired)', tint: 'var(--robot-retired-tint)' },
};

export function LoaderStatusPill({ status }: { status: LoaderStatus }) {
  const s = LOADER_STATUS_TINT[status];
  return <StatusPill color={s.color} label={LOADER_STATUS_LABELS[status]} tint={s.tint} />;
}

const VERIFICATION_MAP: Record<VerificationStatus, { label: string; color: string }> = {
  [VerificationStatus.CONFIRMED]: {
    label: ROBOT_VERIFICATION_LABELS.CONFIRMED,
    color: 'var(--robot-operating)',
  },
  [VerificationStatus.PHANTOM]: {
    label: ROBOT_VERIFICATION_LABELS.PHANTOM,
    color: 'var(--robot-retired)',
  },
  [VerificationStatus.TENTATIVE_OWNERSHIP]: {
    label: ROBOT_VERIFICATION_LABELS.TENTATIVE_OWNERSHIP,
    color: 'var(--dday-warning)',
  },
  [VerificationStatus.MISSING_SERIAL]: {
    label: ROBOT_VERIFICATION_LABELS.MISSING_SERIAL,
    color: 'var(--robot-maintenance)',
  },
};

export function VerificationPill({ status }: { status: VerificationStatus }) {
  const s = VERIFICATION_MAP[status];
  if (status === VerificationStatus.CONFIRMED) return null;
  return <StatusPill color={s.color} label={s.label} />;
}
